/**
 * SIL-473 — Circularity tab data layer. Same `{ data, basis }` hook pattern
 * as `useEnvironmentalOverview.ts`: one shared underlying query
 * (`useCircularityQuery`, `GET /api/client/environmental/circularity`) for
 * the REAL widgets (Sankey repurpose/disposal legs, disposition comparison),
 * plus a synchronous sample hook for the goal bar (no network round trip —
 * see `lib/circularitySampleData.ts`, THE ONE MODULE that fixture lives in).
 *
 * SIL-471: the Sankey's `resale` leg is now HYBRID — `useLifecycleSankey`
 * renders `query.sankey.resale` when the API served a non-null leg (the org
 * has at least one COMPLETED `marketplace_transaction`), falling back to the
 * sample fixture otherwise. Detected by NULLNESS alone, matching the
 * Overview tab's `avoided` field contract — never by an empty/zero leg.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { EnvironmentalBasis, EnvironmentalCircularityResponse } from '@siliconx/shared';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';
import {
  CIRCULARITY_SAMPLE_BASIS,
  SAMPLE_AVOIDED_TO_DATE_KGCO2E,
  SAMPLE_CIRCULARITY_GOAL_KGCO2E,
  SAMPLE_RESALE_AVOIDED_VS_NEW_KGCO2E,
  SAMPLE_RESALE_LEG_ASSET_COUNT,
  SAMPLE_RESALE_LEG_EMBODIED_KGCO2E,
} from '../lib/circularitySampleData';

// ---------------------------------------------------------------------------
// Shared underlying query — REAL widgets only.
// ---------------------------------------------------------------------------

export function useCircularityQuery(): UseQueryResult<EnvironmentalCircularityResponse> {
  return useQuery({
    queryKey: qk.environmental.circularity(),
    queryFn: async () => {
      const res = await api.api.client.environmental.circularity.$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });
}

type WidgetResult<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
};

// ---------------------------------------------------------------------------
// REAL + HYBRID — lifecycle-flows Sankey. `repurpose`/`disposal` are always
// the query's real legs; `resale` is real when `query.sankey.resale` is
// non-null (SIL-471), sample fallback otherwise.
// ---------------------------------------------------------------------------

/** Deliberately no `label` field — leg copy is presentation, owned by
 * `components/environmental/circularity/circularityCopy.ts`, not this data
 * layer. */
export interface SankeyLegView {
  assetCount: number;
  embodiedKgco2e: number;
  basis: EnvironmentalBasis;
}

export function useLifecycleSankey(): WidgetResult<{
  repurpose: SankeyLegView;
  disposal: SankeyLegView & { cat5BurdenKgco2e: number };
  resale: SankeyLegView & { avoidedVsNewKgco2e: number };
}> {
  const q = useCircularityQuery();
  const resale = q.data?.sankey.resale;
  const data = q.data
    ? {
        repurpose: {
          assetCount: q.data.sankey.repurpose.assetCount,
          embodiedKgco2e: q.data.sankey.repurpose.embodiedKgco2e,
          basis: q.data.sankey.repurpose.basis,
        },
        disposal: {
          assetCount: q.data.sankey.disposal.assetCount,
          embodiedKgco2e: q.data.sankey.disposal.embodiedKgco2e,
          cat5BurdenKgco2e: q.data.sankey.disposal.cat5BurdenKgco2e,
          basis: q.data.sankey.disposal.basis,
        },
        resale: resale
          ? {
              assetCount: resale.assetCount,
              embodiedKgco2e: resale.embodiedKgco2e,
              avoidedVsNewKgco2e: resale.avoidedVsNewKgco2e,
              basis: resale.basis,
            }
          : {
              assetCount: SAMPLE_RESALE_LEG_ASSET_COUNT,
              embodiedKgco2e: SAMPLE_RESALE_LEG_EMBODIED_KGCO2E,
              avoidedVsNewKgco2e: SAMPLE_RESALE_AVOIDED_VS_NEW_KGCO2E,
              basis: CIRCULARITY_SAMPLE_BASIS,
            },
      }
    : undefined;

  return { data, isLoading: q.isLoading, isError: q.isError, error: q.error };
}

// ---------------------------------------------------------------------------
// REAL — three-column disposition comparison for the representative gpu
// cohort (median SKU-grain embodied, per the readiness doc).
// ---------------------------------------------------------------------------

export function useDispositionComparison(): WidgetResult<EnvironmentalCircularityResponse['comparison']> {
  const q = useCircularityQuery();
  return { data: q.data?.comparison, isLoading: q.isLoading, isError: q.isError, error: q.error };
}

// ---------------------------------------------------------------------------
// SAMPLE — avoided-to-date vs org goal bar. Synchronous, no network round
// trip. Advisory-vs-advisory is the one pairing the SIL-459 register
// allows — neither figure is a footprint number.
// ---------------------------------------------------------------------------

export function useAvoidedGoalBar(): {
  data: { avoidedToDateKgco2e: number; goalKgco2e: number };
  basis: EnvironmentalBasis;
} {
  return {
    data: { avoidedToDateKgco2e: SAMPLE_AVOIDED_TO_DATE_KGCO2E, goalKgco2e: SAMPLE_CIRCULARITY_GOAL_KGCO2E },
    basis: CIRCULARITY_SAMPLE_BASIS,
  };
}
